import axios, { AxiosInstance, AxiosError } from 'axios';
import { resolvePublicApiBaseUrl } from '@/lib/api-base-url';

/**
 * - 生产：默认同域根路径（base 为空），请求 URL 以 `/api/...` 开头，浏览器只出现一层 `/api`
 * - 开发：默认同上，走 Vite `server.proxy['/api']`
 * - 跨域：设置 VITE_API_URL 为站点根，如 `https://systemhub.example.com`（不要带 `/api`）
 */
const API_BASE_URL = resolvePublicApiBaseUrl();

// 创建 axios 实例
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 确保跨域请求携带 cookie
});

// 请求拦截器 - 自动添加 token
api.interceptors.request.use(
  (config) => {
    // 从 auth-storage 中获取 token（Zustand persist 存储的位置）
    const authStorage = localStorage.getItem('auth-storage');
    let token: string | null = null;
    
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        token = parsed.state?.token || null;
      } catch (e) {
        console.error('Failed to parse auth-storage:', e);
      }
    }
    
    // 如果没有从 auth-storage 获取到，尝试直接从 token key 获取（向后兼容）
    if (!token) {
      token = localStorage.getItem('token');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 统一错误处理
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token 过期或无效，清除所有状态并重新加载
      localStorage.removeItem('token');
      localStorage.removeItem('auth-storage');
      // 使用 replace 避免浏览器后退按钮回到登录前页面
      window.location.replace('/');
    }
    return Promise.reject(error);
  }
);

// 导出 apiClient 以保持向后兼容
export const apiClient = api;

// API 响应类型
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
}

// 错误类型
export interface ApiError {
  message: string;
  statusCode?: number;
}
